import {
  CButton,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { createTaxPayerApiKey, updateTaxPayerApiKey } from "../../api/taxpayer-api-keys";
import TaxPayerApiKey, {
  PUBLIC_API_PERMISSION_LIST,
  newTaxPayerApiKey,
} from "../../models/taxpayer-api-key";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert } from "../utils/messages";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface TaxPayerApiKeyFormProps {
  apiKey?: TaxPayerApiKey;
  onCancel: () => void | Promise<void>;
  onSuccess: (_?: TaxPayerApiKey) => void | Promise<void>;
}

const TaxPayerApiKeyForm: React.FC<TaxPayerApiKeyFormProps> = ({
  apiKey,
  onCancel,
  onSuccess,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  const isEditing = apiKey !== undefined && apiKey.id !== undefined;
  const apiKeyRef = useRef<TaxPayerApiKey>(apiKey ?? newTaxPayerApiKey(taxPayer.id));
  const [submitting, setSubmitting] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    apiKey?.permissions ?? []
  );
  const [expiresAt, setExpiresAt] = useState<string>(
    apiKey?.expiresAt ? apiKey.expiresAt.toString().substring(0, 10) : ""
  );

  const groups = Array.from(
    new Set(PUBLIC_API_PERMISSION_LIST.map((p) => p.group))
  );

  const togglePermission = (value: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const toggleGroup = (group: string) => {
    const groupPerms = PUBLIC_API_PERMISSION_LIST
      .filter((p) => p.group === group)
      .map((p) => p.value);
    const allSelected = groupPerms.every((p) => selectedPermissions.includes(p));
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((p) => !groupPerms.includes(p)));
    } else {
      setSelectedPermissions((prev) => [
        ...prev,
        ...groupPerms.filter((p) => !prev.includes(p)),
      ]);
    }
  };

  const onSubmit = async () => {
    if (!isEditing && !apiKeyRef.current.keyName) {
      errorAlert("El nombre de la API Key es obligatorio.");
      return;
    }
    if (selectedPermissions.length === 0) {
      errorAlert("Debe seleccionar al menos un permiso.");
      return;
    }
    setSubmitting(true);

    let status: any;
    if (isEditing) {
      status = await updateTaxPayerApiKey(apiKey!.id!, {
        taxPayerId: taxPayer.id,
        permissions: selectedPermissions,
        expiresAt: expiresAt || undefined,
      });
    } else {
      const payload: TaxPayerApiKey = {
        ...apiKeyRef.current,
        taxPayerId: taxPayer.id,
        permissions: selectedPermissions,
        expiresAt: expiresAt || undefined,
      };
      status = await createTaxPayerApiKey(payload);
    }

    if (status.status !== SUCCESS) {
      const msg = status.detail ?? (isEditing ? "Error al actualizar la API Key." : "Error al crear la API Key.");
      errorAlert(msg);
      setSubmitting(false);
      return;
    }
    onSuccess(status.data as TaxPayerApiKey);
    setSubmitting(false);
  };

  return (
    <>
      {!isEditing && (
        <CRow>
          <CCol sm="12">
            <div className="alert alert-info mb-3">
              <i className="fa fa-key mr-2"></i>
              <strong>¿Para qué sirven las API Keys?</strong> Permiten que aplicaciones externas accedan a la API de tu cuenta de forma segura, sin exponer tus credenciales. Podés generar una clave por integración y revocarla en cualquier momento sin afectar a las demás.
            </div>
          </CCol>
        </CRow>
      )}
      <CRow>
        {!isEditing ? (
          <CCol sm="12" md="6">
            <CFormGroup>
              <CLabel>Nombre</CLabel>
              <CInput
                type="text"
                placeholder="Ej: Integración ERP"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  apiKeyRef.current.keyName = e.target.value;
                }}
              />
            </CFormGroup>
          </CCol>
        ) : (
          <CCol sm="12" md="6">
            <CFormGroup>
              <CLabel>Nombre</CLabel>
              <CInput
                type="text"
                value={apiKey!.keyName ?? ""}
                readOnly
                disabled
              />
            </CFormGroup>
          </CCol>
        )}
        <CCol sm="12" md="6">
          <CFormGroup>
            <CLabel>Fecha de expiración (opcional)</CLabel>
            <CInput
              type="date"
              value={expiresAt}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setExpiresAt(e.target.value);
              }}
            />
          </CFormGroup>
        </CCol>
      </CRow>

      <CRow>
        <CCol sm="12">
          <CLabel>
            <strong>Permisos</strong>
          </CLabel>
          <CRow>
            {groups.map((group) => {
              const groupPerms = PUBLIC_API_PERMISSION_LIST.filter(
                (p) => p.group === group
              );
              const allSelected = groupPerms.every((p) =>
                selectedPermissions.includes(p.value)
              );
              return (
                <CCol key={group} sm="12" md="6" lg="4" className="mb-3">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center mb-1">
                      <input
                        type="checkbox"
                        id={`group-${group}`}
                        checked={allSelected}
                        onChange={() => toggleGroup(group)}
                        className="mr-2"
                        style={{ cursor: "pointer" }}
                      />
                      <CLabel
                        htmlFor={`group-${group}`}
                        className="mb-0 font-weight-bold"
                        style={{ cursor: "pointer" }}
                      >
                        {group}
                      </CLabel>
                    </div>
                    {groupPerms.map((perm) => (
                      <div
                        key={perm.value}
                        className="d-flex align-items-center ml-3"
                      >
                        <input
                          type="checkbox"
                          id={`perm-${perm.value}`}
                          checked={selectedPermissions.includes(perm.value)}
                          onChange={() => togglePermission(perm.value)}
                          className="mr-2"
                          style={{ cursor: "pointer" }}
                        />
                        <CLabel
                          htmlFor={`perm-${perm.value}`}
                          className="mb-0"
                          style={{ cursor: "pointer" }}
                        >
                          {perm.label}
                        </CLabel>
                      </div>
                    ))}
                  </div>
                </CCol>
              );
            })}
          </CRow>
        </CCol>
      </CRow>

      <CRow className="mt-3">
        <CCol sm="12" className="d-flex justify-content-end">
          <CButton
            color="secondary"
            className="mr-2"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </CButton>
          <CButton color="primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <></>
            )}
            {submitting
              ? isEditing
                ? "Guardando..."
                : "Creando..."
              : isEditing
              ? "Guardar cambios"
              : "Crear API Key"}
          </CButton>
        </CCol>
      </CRow>
    </>
  );
};

export default TaxPayerApiKeyForm;
